import React from 'react';

import { Button, buttonClasses, Grid2 as Grid, styled } from '@mui/material';

import ResponsiveGrid from '../../../layout/grid';
import { MarkdownContainer } from '../../markdown/markdown-container';
import { ConversionTeaserProps } from '../conversion-teaser/conversion-teaser';
import { ConversionTeaserBase } from '../types';

export type ConversionTeaserImageProps<T extends React.ElementType> = Omit<
  ConversionTeaserBase<T> & ConversionTeaserProps,
  'headline'
> & {
  headline: React.ReactElement | string;
  className?: string;
};

export const ConversionTeaserImage = <T extends React.ElementType>({
  headline,
  labelCTA,
  content,
  state,
  sx,
  image,
  link,
  className = '',
}: ConversionTeaserImageProps<T>) => {
  return (
    <Wrapper sx={sx} className={className}>
      <ContainerGrid size={12}>
        <ImageContainer>
          {image}
          <GradientContainer />
        </ImageContainer>
      </ContainerGrid>

      <TeaserResponsiveGrid gridProps={{ sx: { height: '100%' } }}>
        <TeaserGrid size={{ xs: 12, sm: 5 }} offset={{ sm: 6 }}>
          <TeaserContent>
            <Headline>{headline}</Headline>
            {content ? <ContentWrapper html={content} /> : null}
            <ButtonStyleWrapper>
              <Button
                variant="contained"
                color="primary"
                component={link.component}
                {...link.props}
                state={state}
              >
                {labelCTA}
              </Button>
            </ButtonStyleWrapper>
          </TeaserContent>
        </TeaserGrid>
      </TeaserResponsiveGrid>
    </Wrapper>
  );
};

const Wrapper = styled(ResponsiveGrid)(({ theme }) => ({
  position: 'relative',
}));

const ContainerGrid = styled(Grid)(({ theme }) => ({
  position: 'relative',
  borderRadius: 'var(--rounded-corners)',
  overflow: 'hidden',

  [theme.breakpoints.up('sm')]: {
    '.gatsby-image-wrapper': {
      height: '100%',
    },
  },
}));

const ImageContainer = styled('div')(({ theme }) => ({
  lineHeight: 0,
  aspectRatio: '335 / 575',

  'img, .gatsby-image-wrapper': {
    height: '100%',
    objectFit: 'cover',
  },

  [theme.breakpoints.up('sm')]: {
    aspectRatio: '750 / 450',
  },

  [theme.breakpoints.up('md')]: {
    aspectRatio: '1289 / 650',
  },

  [theme.breakpoints.up('lg')]: {
    aspectRatio: '1722 / 750',
  },
}));

const GradientContainer = styled('div')(({ theme }) => ({
  background:
    'linear-gradient(90deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 100%)',
  inset: '0 0 0 25%',
  position: 'absolute',
  opacity: 0.5,
  right: 0,

  [theme.breakpoints.down('sm')]: {
    background:
      'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 100%)',
    inset: '33% 0 0 0',
  },
}));

const TeaserResponsiveGrid = styled(ResponsiveGrid)(({ theme }) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  width: '100%',
  height: '100%',
}));

const TeaserGrid = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.up('sm')]: {
    display: 'flex',
    height: 'calc(100% - 60px)',
    marginTop: '60px',
  },

  [theme.breakpoints.up('md')]: {
    marginTop: '80px',
    height: 'calc(100% - 80px)',
  },

  [theme.breakpoints.up('lg')]: {
    marginTop: '100px',
    height: 'calc(100% - 100px)',
  },
}));

const TeaserContent = styled('div')(({ theme }) => ({
  [theme.breakpoints.only('xs')]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    height: '100%',
    padding: '0 20px 40px',
    color: 'var(--color-white)',
  },
}));

const Headline = styled('h2')(({ theme }) => ({
  ...theme.typography.h3,
  fontWeight: theme.typography.fontWeightRegular,
  marginTop: '16px',
  marginBottom: '5px',

  strong: {
    fontWeight: theme.typography.fontWeightMedium,
  },

  [theme.breakpoints.up('sm')]: {
    marginBottom: '8px',
    color: 'var(--color-white)',
  },

  [theme.breakpoints.up('md')]: {
    marginTop: '0px',
    marginBottom: '10px',
  },

  [theme.breakpoints.up('lg')]: {
    marginBottom: '12px',
  },
}));

const ButtonStyleWrapper = styled('div')(({ theme }) => ({
  marginTop: 'var(--spacer-l)',

  [`.${buttonClasses.contained}`]: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
}));

const ContentWrapper = styled(MarkdownContainer)(({ theme }) => ({
  ...theme.typography.h5,
  fontWeight: theme.typography.fontWeightRegular,
  paddingRight: '30px',

  [theme.breakpoints.up(0)]: {
    marginBottom: 0,
  },

  [theme.breakpoints.up('sm')]: {
    padding: 0,
    color: 'var(--color-white)',
  },
}));
